import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  MemberCommitment,
  MemberByCode,
  MemberInfo,
  MemberSummary,
  CommitmentTimeline,
  MemberCollaborationsResponse,
  UserHistoryResponse,
} from '../models/impact-panel.model';

@Injectable({
  providedIn: 'root',
})
export class ImpactPanelService {
  // Cache por member code: varias colaboraciones con el mismo miembro comparten una sola llamada.
  private memberByCodeCache = new Map<string, Observable<MemberByCode | null>>();

  // Cache de sesión por URL lógica: al volver al panel se emite al instante lo ya traído y se
  // refresca por detrás. Si el refresco falla y había cache, se conserva lo mostrado.
  private sessionCache = new Map<string, unknown>();

  constructor(private httpClient: HttpClient) {}

  private cached<T>(key: string, fetch: () => Observable<T>): Observable<T> {
    return new Observable<T>(subscriber => {
      const hit = this.sessionCache.has(key);
      if (hit) subscriber.next(this.sessionCache.get(key) as T);
      const inner = fetch().subscribe({
        next: value => {
          this.sessionCache.set(key, value);
          subscriber.next(value);
        },
        error: err => (hit ? subscriber.complete() : subscriber.error(err)),
        complete: () => subscriber.complete(),
      });
      return () => inner.unsubscribe();
    });
  }

  // La wallet del usuario logueado sale de `members/:id` (api_fibras) usando el id de miembro
  // que viene en el token; no hace falta descargar las listas completas.
  public getMemberInfoById(memberId: number | string): Observable<MemberInfo | null> {
    // Sin catchError: un 500 o un corte debe llegar a la página como error (con "Reintentar"),
    // no confundirse con un usuario sin wallet.
    return this.fetchMember(String(memberId)).pipe(
      map(member => {
        if (!member?.wallet_address) return null;
        return {
          id: member.id,
          email: member.email || '',
          type: (member.type || '').toLowerCase(),
          name: member.name || '',
          surname: member.lastName || undefined,
          walletAddress: member.wallet_address,
        };
      }),
    );
  }

  private fetchMember(key: string): Observable<MemberByCode | null> {
    return this.httpClient
      .get<MemberByCode[]>(environment.api_fibras + `members/${encodeURIComponent(key)}`)
      .pipe(map(members => members?.[0] ?? null));
  }

  public getMemberByCode(memberCode: number | string): Observable<MemberByCode | null> {
    const key = String(memberCode);
    const cached = this.memberByCodeCache.get(key);
    if (cached) return cached;

    const request$ = this.fetchMember(key).pipe(
      catchError(() => {
        // No se cachea el error, para poder reintentar en el próximo expand.
        this.memberByCodeCache.delete(key);
        return of(null);
      }),
      shareReplay(1),
    );
    this.memberByCodeCache.set(key, request$);
    return request$;
  }

  public getMemberSummary(walletAddress: string): Observable<MemberSummary> {
    return this.cached(`summary:${walletAddress}`, () =>
      this.httpClient.get<MemberSummary>(
        environment.api_sincronia + `members/${walletAddress}/summary`,
      ),
    );
  }

  public getMemberCommitments(
    walletAddress: string,
  ): Observable<{ walletAddress: string; total: number; commitments: MemberCommitment[] }> {
    return this.cached(`commitments:${walletAddress}`, () =>
      this.httpClient.get<{
        walletAddress: string;
        total: number;
        commitments: MemberCommitment[];
      }>(environment.api_sincronia + `members/${walletAddress}/commitments`),
    );
  }

  public getMemberCollaborations(
    walletAddress: string,
  ): Observable<MemberCollaborationsResponse> {
    return this.cached(`collaborations:${walletAddress}`, () =>
      this.httpClient.get<MemberCollaborationsResponse>(
        environment.api_sincronia + `members/${walletAddress}/collaborations`,
      ),
    );
  }

  public getCommitmentTimeline(
    walletAddress: string,
    commitmentCode: number | string,
    limit = 20,
    offset = 0,
  ): Observable<CommitmentTimeline> {
    const params = new HttpParams()
      .set('limit', limit)
      .set('offset', offset);
    const request = () =>
      this.httpClient.get<CommitmentTimeline>(
        environment.api_sincronia +
          `members/${walletAddress}/commitments/${commitmentCode}`,
        { params },
      );
    // Solo la primera página se cachea; "cargar más" siempre va a la red.
    return offset === 0
      ? this.cached(`timeline:${walletAddress}:${commitmentCode}:${limit}`, request)
      : request();
  }

  public getUserHistory(
    walletAddress: string,
    receivedCodes?: string,
    limit?: number,
    offset?: number,
  ): Observable<UserHistoryResponse> {
    let params = new HttpParams();
    if (receivedCodes) params = params.set('receivedCodes', receivedCodes);
    if (limit != null) params = params.set('limit', limit);
    if (offset != null) params = params.set('offset', offset);
    const request = () =>
      this.httpClient.get<UserHistoryResponse>(
        environment.api_sincronia + `users/${walletAddress}/history`,
        { params },
      );
    return !receivedCodes && !offset
      ? this.cached(`history:${walletAddress}:${limit ?? ''}`, request)
      : request();
  }
}
